import { ReactElement, SyntheticEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { NOT_REAL_SPORTS } from 'utils/constants/sportsbook/sportsbook-data-hierarchy';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { isMobile } from 'utils/is-mobile';
import { TimeFilterWrapper } from './TimeFilterWrapper';
import { SportsFavoriteButton } from 'newcomponents/UI/Menu/components/FavoriteButton';
import { Skeleton } from 'newcomponents/UI/Skeleton';
import { useSportsFavoriteClickHandler } from 'hooks/favorite/useSportsFavoriteClickHandler';
import { clearAllFavorite } from 'store/actions';
import { getFavoriteEntitiesCount } from 'store/selectors/fav-data';
import { getSportsbookSelectedSport } from 'store/selectors/new-sportsbook';
import './index.less';

if (SpringConfigs.IS_RTL) {
  import('./rtl.less');
}

type TSportsHeaderProps = {
  sportAlias: string;
  externalTitle?: string;
  showTimeFilter?: boolean;
  showFavoriteButton?: boolean;
};

export const SportsHeader = ({
  sportAlias,
  externalTitle,
  showFavoriteButton,
  showTimeFilter = true
}: TSportsHeaderProps): ReactElement => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selectedSport = useSelector(getSportsbookSelectedSport);
  const sportsFavoriteClickHandler = useSportsFavoriteClickHandler();
  const favoritesCount = useSelector(
    getFavoriteEntitiesCount
  ).sportsWithCompetitions;

  const showClearAllButton = !!(
    (sportAlias === 'favorite' || sportAlias === 'favourites') &&
    favoritesCount
  );

  const shouldShowTimeFilter =
    showTimeFilter && !NOT_REAL_SPORTS.includes(sportAlias);

  const isLoading = !externalTitle && !selectedSport;

  const clearAllFavorites = (e: SyntheticEvent) => {
    e.stopPropagation();
    dispatch(clearAllFavorite('sports'));
    dispatch(clearAllFavorite('competitions'));
  };

  const title = useMemo(() => {
    if (externalTitle) {
      return externalTitle;
    }

    if (selectedSport) {
      const gameCount = isMobile()
        ? selectedSport.game || selectedSport.gameCount
        : 0;

      return `${selectedSport?.name} ${gameCount ? `(${gameCount})` : ''} `;
    }
  }, [selectedSport, externalTitle]);

  if (shouldShowTimeFilter) {
    return (
      <TimeFilterWrapper
        title={title}
        isLoading={
          !SpringConfigs.MOCKED_DATA && (isLoading || !title || !sportAlias)
        }
        sport={sportAlias}
        showSortButton
      />
    );
  }

  return (
    <div className="sportsHeader-titleWrapper">
      <div className="sportsHeader-titleInner">
        <span
          className={`sportsHeader-title sportsHeader-title-${
            isMobile() ? 'mobile' : 'desktop'
          }`}
        >
          {selectedSport && showFavoriteButton && (
            <SportsFavoriteButton
              onClick={sportsFavoriteClickHandler}
              sportId={selectedSport.id}
            />
          )}
          {title || (
            <Skeleton
              title={{
                style: { width: '50%', height: '20px', margin: 0 }
              }}
              paragraph={false}
              active
            />
          )}
        </span>
        {showClearAllButton && (
          <span
            className="sportsHeader-title-favorite__clear-all"
            data-testid="clear-all"
            onClick={clearAllFavorites}
          >
            {t('sportsbook.clearAll')}
          </span>
        )}
      </div>
    </div>
  );
};
